import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import PropTypes from 'prop-types';

import { ADD_PROJECT } from '../../../apis/project';
import { CREATE_WORKSPACE, CREATE_WORKSPACE_PROFILE, UPDATE_WORKSPACE, UPDATE_WORKSPACE_PROFILE } from '../../../apis/workspace';
import { navigateToWorkspaceUrl } from '../../../utils/navigator';
import { PROJECT_KEY_REGEX, WORKSPACE_CREATION_STEPS, WORKSPACE_HANDLE_REGEX, WORKSPACE_NAME_REGEX } from '../../../constants/workspaces';
import { PROJECT, PROJECTS } from '../../../constants/routes';
import { uploadWorkspaceAvatar, uploadWorkspaceProfileAvatar } from '../../../utils/upload-avatar';
import Button from '../../../components/atoms/button';
import Modal from '../../../components/organisms/modal';
import useAuth from '../../../context/AuthContext';
import useLoader from '../../../context/LoaderContext';
import useWorkspaceCreation from '../../../context/WorkspaceCreationContext';

// regex to validate the workspace name
const workspaceNameRegex = new RegExp(WORKSPACE_NAME_REGEX);

// regex to validate the workspace handle
const workspaceHandleRegex = new RegExp(WORKSPACE_HANDLE_REGEX);

// regex to validate the project key
const projectKeyRegex = new RegExp(PROJECT_KEY_REGEX);

const WorkspaceCreationFooter = (props) => {
  // init
  const { step, setStep, setToast } = props;
  const { authUser } = useAuth();
  const { setIsLoading } = useLoader();
  const {
    workspaceName,
    workspaceHandle,
    workspaceId,
    workspaceProfileId,
    setWorkspaceId,
    setWorkspaceProfileId,
    setProjectId,
    workspaceLogo,
    workspaceProfileLogo,
    workspaceProfileName,
    projectName,
    projectKey,
    projectDescription,
    errors,
    addError,
    removeError,
    clearForm,
  } = useWorkspaceCreation();
  const navigate = useNavigate();

  // mutations/queries
  const [addWorkspace] = useMutation(CREATE_WORKSPACE);
  const [addWorkspaceProfile] = useMutation(CREATE_WORKSPACE_PROFILE);
  const [addProject] = useMutation(ADD_PROJECT);
  const [updateWorkspaceMutation] = useMutation(UPDATE_WORKSPACE);
  const [updateWorkspaceProfileMutation] = useMutation(UPDATE_WORKSPACE_PROFILE);

  // states
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  // validate workspace basic details
  const validateBasicDetails = () => {
    if (workspaceNameRegex.test(workspaceName)) {
      removeError('workspaceName');
      return true;
    }
    addError('workspaceName', 'Workspace name must be between 8 and 36 characters and must not include any special characters.');
    return false;
  };

  const validateWorkspaceHandle = () => {
    if (workspaceHandleRegex.test(workspaceHandle) && !errors.handleUnavailable) {
      removeError('workspaceHandle');
      return true;
    }
    return false;
  };

  // validate workspace profile details
  const validateProfileDetails = () => {
    if (workspaceProfileName) {
      removeError('workspaceProfileName');
      return true;
    }
    addError('workspaceProfileName', 'Profile name is required.');
    return false;
  };

  const validateProjectDetails = () => {
    let isValid = true;

    if (!projectKey) {
      addError('projectKey', 'Project key is required.');
      isValid = false;
    }

    if (!projectKeyRegex.test(projectKey)) {
      addError('projectKey', 'Project key must be 2 to 4 characters long and must be uppercase and alphanumeric.');
      isValid = false;
    }

    if (!projectName) {
      addError('projectName', 'Project name is required.');
      isValid = false;
    }

    if (isValid) {
      removeError('projectKey');
      removeError('projectName');
    }

    return isValid;
  };

  // Go to URL Handle Step if basic details are valid
  const goToUrlHandleStep = () => { if (validateBasicDetails()) setStep(WORKSPACE_CREATION_STEPS.URL_HANDLE); };

  // Go to Profile Details Step if URL Handle is valid
  const goToProfileDetailsStep = () => { if (validateWorkspaceHandle()) setStep(WORKSPACE_CREATION_STEPS.PROFILE_DETAILS); };

  const createWorkspace = async () => {
    try {
      const variables = {
        input: {
          name: workspaceName,
          handle: workspaceHandle,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      };
      const { data: { createWorkspace: { id } } } = await addWorkspace({ variables });
      setWorkspaceId(id);
      return id;
    } catch (error) {
      throw new Error('Something went wrong while creating workspace.');
    }
  };

  const updateWorkspace = async (newWorkspaceId) => {
    try {
      const variables = {
        input: {
          id: newWorkspaceId || workspaceId,
          name: workspaceName,
          updatedAt: new Date().toISOString(),
        },
      };
      await updateWorkspaceMutation({ variables, context: { headers: { Authority: workspaceId } } });
    } catch (error) {
      throw new Error('Something went wrong while updating workspace.');
    }
  };

  const createWorkspaceProfile = async (newWorkspaceId) => {
    try {
      const variables = {
        input: {
          workspaceId: newWorkspaceId || workspaceId,
          userId: authUser?.id,
          isActive: true,
          displayName: workspaceProfileName,
        },
      };
      const { data: { createWorkspaceProfile: { id } } } = await addWorkspaceProfile({ variables });
      setWorkspaceProfileId(id);
      return id;
    } catch (error) {
      throw new Error('Something went wrong while creating workspace profile.');
    }
  };

  const updateWorkspaceProfile = async (newWorkspaceId, newProfileId) => {
    try {
      const variables = {
        input: {
          id: newProfileId || workspaceProfileId,
          workspaceId: newWorkspaceId || workspaceId,
          displayName: workspaceProfileName,
        },
      };
      await updateWorkspaceProfileMutation({ variables, context: { headers: { Authority: workspaceId } } });
    } catch (error) {
      throw new Error('Something went wrong while updating workspace profile.');
    }
  };

  // Create Workspace Profile using addWorkspaceProfile mutation
  const createOrUpdateWorkspaceAndWorkspaceProfile = async () => {
    if (validateProfileDetails() && validateBasicDetails() && validateWorkspaceHandle()) {
      try {
        setIsLoading(true);
        let newWorkspaceId = workspaceId;
        if (!newWorkspaceId) newWorkspaceId = await createWorkspace();
        else await updateWorkspace(newWorkspaceId);
        let newProfileId = workspaceProfileId;
        if (!newProfileId) newProfileId = await createWorkspaceProfile(newWorkspaceId);
        else await updateWorkspaceProfile(newWorkspaceId, newProfileId);
        setStep(WORKSPACE_CREATION_STEPS.PROJECT_DETAILS);
      } catch (error) {
        setToast((prevstate) => ({ ...prevstate, message: error.message, open: true, type: 'error' }));
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Create Project using addProject mutation
  const createProject = async () => {
    try {
      setIsLoading(true);
      await uploadWorkspaceProfileAvatar({ workspaceProfileLogo, userId: authUser?.id, workspaceProfileId, workspaceId });
      await uploadWorkspaceAvatar({ workspaceLogo, workspaceId });
      if (!projectKey && !projectName && !projectDescription) {
        clearForm();
        navigateToWorkspaceUrl(workspaceHandle, `/${PROJECTS}`);
      } else if (validateProjectDetails()) {
        try {
          const variables = {
            input: {
              name: projectName,
              key: projectKey,
              description: projectDescription,
              workspaceId,
              isActive: true,
            },
          };
          const { data: { addProject: { id } } } = await addProject({ variables, context: { headers: { Authority: workspaceId } } });
          setProjectId(id);
          navigateToWorkspaceUrl(workspaceHandle, `/${PROJECT}/${id}`);
        } catch (error) {
          throw Error('Something went wrong while creating project!');
        }
      }
    } catch (error) {
      setToast((prevstate) => ({ ...prevstate, message: error.message, open: true, type: 'error' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextOrFinish = async () => {
    switch (step) {
      case WORKSPACE_CREATION_STEPS.BASIC_DETAILS: goToUrlHandleStep(); break;
      case WORKSPACE_CREATION_STEPS.URL_HANDLE: goToProfileDetailsStep(); break;
      case WORKSPACE_CREATION_STEPS.PROFILE_DETAILS: createOrUpdateWorkspaceAndWorkspaceProfile(); break;
      case WORKSPACE_CREATION_STEPS.PROJECT_DETAILS: createProject(); break;
      default: break;
    }
  };

  const handleBack = () => {
    switch (step) {
      case WORKSPACE_CREATION_STEPS.URL_HANDLE: setStep(WORKSPACE_CREATION_STEPS.BASIC_DETAILS); break;
      case WORKSPACE_CREATION_STEPS.PROFILE_DETAILS: setStep(WORKSPACE_CREATION_STEPS.URL_HANDLE); break;
      case WORKSPACE_CREATION_STEPS.PROJECT_DETAILS: setStep(WORKSPACE_CREATION_STEPS.PROFILE_DETAILS); break;
      default: break;
    }
  };

  const handleCancel = () => {
    if (window.history.length === 1) window.close();
    else navigate(-1);
    clearForm();
  };

  return (
    <>
      <div className="workspace-creation-footer">
        <span className="divider" />
        <div className="workspace-creation-action-buttons">
          <Button onClick={() => setIsCancelDialogOpen(true)} primary outlined>Cancel</Button>
          <div className="step-change-buttons">
            {step !== WORKSPACE_CREATION_STEPS.BASIC_DETAILS && <Button primary outlined onClick={handleBack}>Back</Button>}
            <Button primary onClick={handleNextOrFinish}>{step === WORKSPACE_CREATION_STEPS.PROJECT_DETAILS ? 'Finish' : 'Next'}</Button>
          </div>
        </div>
      </div>
      <Modal
        title="Leave Workspace Creation"
        body="Are you sure you want to leave workspace creation? This cannot be undone."
        secondaryButton={(<Button onClick={handleCancel} danger>Leave</Button>)}
        primaryButton={(<Button primary outlined onClick={() => setIsCancelDialogOpen(false)}>Stay in Workspace Creation</Button>)}
        isOpen={isCancelDialogOpen}
        actionAlignment="right"
        setIsOpen={() => setIsCancelDialogOpen((prev) => !prev)}
      />
    </>
  );
};

WorkspaceCreationFooter.propTypes = {
  step: PropTypes.symbol.isRequired,
  setStep: PropTypes.func.isRequired,
  setToast: PropTypes.func.isRequired,
};

export default WorkspaceCreationFooter;
